'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { useState, type ReactNode } from 'react';

import { ACCOUNT_COPY } from '@eliadmin/core/src/components/dlz/account/account-copy';
import { AccountNav } from '@eliadmin/core/src/components/dlz/account/account-nav';
import {
  useAccountPage,
  useOrderParam,
} from '@eliadmin/core/src/components/dlz/account/account-pages';
import { DialogCard } from '@eliadmin/core/src/components/dlz/dialogs/dialog-card';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

/**
 * Phones and tablets (Figma „Môj profil · Mobile“ 60459:89417): „‹ Môj profil“ in DLZ/Heading/H5
 * text/brand instead of the breadcrumb and the side navigation. The design has no account menu
 * screen, so the link opens the account navigation in a dialog.
 */
function AccountMenu({ label }: { label: string }) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger
        aria-label={ACCOUNT_COPY.nav.openMenu}
        className="flex w-full items-center gap-2.5 rounded-sm text-left text-[#D7191F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] lg:hidden"
      >
        <Icon name="caret-left" size={20} />
        <span className="dlz-h5 min-w-0 flex-1">{label}</span>
      </Dialog.Trigger>
      <DialogCard title={ACCOUNT_COPY.account} className="max-w-[427px]">
        <AccountNav framed={false} onNavigate={() => setOpen(false)} />
      </DialogCard>
    </Dialog.Root>
  );
}

/**
 * Body of the account pages (Figma „Body“ 60321:19076): breadcrumb „Domov › Moje konto › page“
 * 40 px under the header, 32 px below it the 427 px navigation and the content column 24 px apart.
 * Under `lg` the navigation moves into the „‹ page“ menu. The order detail draws its own back link,
 * so it has none.
 */
export function AccountShell({ children }: { children: ReactNode }) {
  const page = useAccountPage();
  const order = useOrderParam();
  if (!page) return null;

  const detail = page.key === 'orders' && !!order;
  const breadcrumb = {
    name: ACCOUNT_COPY.account,
    slug: ROUTES.PROFILE(),
    child: {
      name: page.label,
      slug: page.href(),
      ...(detail ? { child: { name: ACCOUNT_COPY.orderCrumb(order), slug: null } } : {}),
    },
  };

  return (
    <div className="mx-auto flex w-full max-w-[1392px] flex-col gap-6 px-4 pb-6 pt-12 md:px-6 lg:gap-8 lg:px-8 lg:pb-10 lg:pt-10">
      <Breadcrumbs
        breadcrumb={breadcrumb}
        className={{ wrapper: 'hidden px-0 md:px-0 lg:block lg:px-0' }}
      />
      {detail ? null : <AccountMenu label={page.label} />}
      <div className="grid items-start gap-6 lg:grid-cols-[427px_minmax(0,1fr)]">
        <aside className="hidden lg:block">
          <AccountNav />
        </aside>
        <div className="flex min-w-0 flex-col">{children}</div>
      </div>
    </div>
  );
}
